import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StoryLabels, StoryLang } from '@org/ui';

/**
 * The story viewer's own texts ("SHE'S HERE", "START CLASS 1 →"…) for the
 * live preview. The preview shows the story in English or Portuguese whatever
 * language the admin UI is in, so the labels are read straight from that
 * language's `admin.json` (`women.storyLabels`) — each locale file only holds
 * its own language, nothing is duplicated. Both languages are always bundled
 * (see `@org/i18n` resources).
 */
export function useStoryLabels(lang: StoryLang): StoryLabels {
  const { i18n } = useTranslation('admin');
  return useMemo(() => {
    const read = (lng: StoryLang): Partial<StoryLabels> => {
      const bundle = i18n.getResourceBundle(lng, 'admin') as
        | { women?: { storyLabels?: Partial<StoryLabels> } }
        | undefined;
      return bundle?.women?.storyLabels ?? {};
    };
    // English fills any label a translation is missing.
    return { ...read('en'), ...read(lang) } as StoryLabels;
  }, [i18n, lang]);
}
